import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { BookOpen, CheckCircle2, Copy, Sparkles, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useApp } from '@/contexts/app-context';
import { aiWritingService } from '@/services/ai-service';
import type { SceneCard } from './plan-scenes-step';

interface ReviewStepProps {
  chapterName: string;
  chapterDescription?: string;
  scenes: SceneCard[];
  onSave: () => void;
  isSaving: boolean;
  projectId?: string;
  storyId?: string;
}

export function ReviewStep({
  chapterName,
  chapterDescription,
  scenes,
  onSave,
  isSaving,
  projectId: propProjectId,
  storyId: propStoryId,
}: ReviewStepProps) {
  const { projectId: paramProjectId } = useParams();
  const projectId = propProjectId || paramProjectId;
  const storyId = propStoryId;
  const { tenantId } = useApp();
  
  const [openingParagraph, setOpeningParagraph] = useState('');
  const [isGeneratingIdea, setIsGeneratingIdea] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchOpeningIdea = useCallback(async () => {
    if (isGeneratingIdea) return;
    
    setIsGeneratingIdea(true);
    try {
      const mappedBeats = scenes.map((s, index) => ({
          name: s.title,
          description: s.description,
          displayOrder: index + 1
      }));

      const result = await aiWritingService.autocomplete(
        '', // Empty cursor context to indicate start of chapter
        chapterName,
        chapterDescription || '',
        mappedBeats,
        projectId || '',
        storyId || '',
        tenantId || undefined
      );

      if (result && result.content) {
          setOpeningParagraph(result.content);
      } else {
          setOpeningParagraph(generatePlaceholderParagraph(chapterName, chapterDescription, scenes));
      }
    } catch (e) {
      console.error('Failed to generate opening idea:', e);
      setOpeningParagraph(generatePlaceholderParagraph(chapterName, chapterDescription, scenes));
    } finally {
      setIsGeneratingIdea(false);
    }
  }, [chapterName, chapterDescription, scenes, projectId, storyId, tenantId]);

  useEffect(() => {
    fetchOpeningIdea();
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(openingParagraph);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = openingParagraph;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col gap-8 w-full max-w-4xl mx-auto"
    >
      {/* Header */}
      <div className="space-y-2 text-center border-b border-border/40 pb-6">
        <h2 className="text-3xl font-bold tracking-tight text-foreground font-lora">Review Your Plan</h2>
        <p className="text-[15px] text-muted-foreground max-w-xl mx-auto leading-relaxed">
          Here's a summary of your chapter plan. Review the details below before saving to start writing.
        </p>
      </div>

      {/* Chapter & Beat Summary Card */}
      <div className="space-y-6">
        <Card className="rounded-2xl border-border bg-card/50 shadow-sm overflow-hidden">
          <div className="p-8 text-center space-y-4">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20 shadow-inner">
              <BookOpen className="h-6 w-6 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-foreground font-lora">{chapterName}</h3>
              {chapterDescription && (
                <p className="text-sm text-muted-foreground max-w-lg mx-auto leading-relaxed">
                  {chapterDescription}
                </p>
              )}
            </div>
          </div>

          {/* Scenes List */}
          <div className="border-t border-border/40 bg-muted/5">
            <div className="px-6 py-4 flex items-center justify-center border-b border-border/40">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/70">
                {scenes.length} {scenes.length === 1 ? 'Scene' : 'Scenes'} Planned
              </span>
            </div>
            <div className="divide-y divide-border/40 max-h-[300px] overflow-y-auto scrollbar-hide">
              {scenes.map((scene, index) => (
                <div key={scene.id} className="flex gap-5 px-8 py-5 hover:bg-muted/10 transition-colors">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shadow-inner flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-[15px] font-semibold text-foreground block">{scene.title}</span>
                    {scene.description && (
                      <span className="text-sm text-muted-foreground leading-relaxed block mt-1">{scene.description}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Opening Paragraph Suggestion */}
        <Card className="rounded-2xl border-primary/20 bg-primary/[0.02] shadow-sm overflow-hidden">
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Sparkles className="h-4 w-4 text-primary/60" />
                <span className="text-xs font-bold uppercase tracking-wider text-foreground">Opening Paragraph Idea</span>
                <AnimatePresence>
                  {isGeneratingIdea && (
                    <motion.span 
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="text-[10px] font-bold uppercase tracking-widest text-primary/60 bg-primary/5 border border-primary/10 px-2 py-0.5 rounded-full flex items-center gap-1.5"
                    >
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      Thinking...
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchOpeningIdea}
                  disabled={isGeneratingIdea}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-primary transition-all rounded-xl"
                  title="Regenerate Idea"
                >
                  <RefreshCw className={cn("h-3.5 w-3.5", isGeneratingIdea && "animate-spin")} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  disabled={isGeneratingIdea || !openingParagraph}
                  className={cn(
                    "h-8 text-[11px] font-semibold gap-2 px-3 rounded-xl transition-all",
                    copied
                      ? "text-emerald-600 bg-emerald-500/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  {copied ? (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      Copy to Editor
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <div className="relative min-h-[120px] bg-background/50 rounded-2xl border border-border/40 shadow-inner overflow-hidden">
              <AnimatePresence mode="wait">
                {isGeneratingIdea ? (
                  <motion.div 
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6"
                  >
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                          className="w-1.5 h-1.5 rounded-full bg-primary"
                        />
                      ))}
                    </div>
                    <span className="text-[11px] text-muted-foreground font-medium animate-pulse">Drafting your first paragraph...</span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-6 text-[15px] leading-relaxed text-foreground/80 font-serif italic"
                  >
                    "{openingParagraph}"
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            <p className="text-[11px] text-muted-foreground/60 text-center italic">
              This suggested opening stays true to your first scene's context.
            </p>
          </div>
        </Card>
      </div>

      {/* Action Area */}
      <div className="space-y-4 pt-4">
        <Button
          onClick={onSave}
          disabled={isSaving}
          className="w-full h-12 rounded-2xl font-bold text-[15px] gap-2 shadow-lg shadow-primary/10 transition-all hover:scale-[1.01] active:scale-[0.99]"
        >
          {isSaving ? (
            'Saving Plan...'
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4" />
              Finalize & Start Writing
              <ArrowRight className="h-4 w-4 ml-1" />
            </>
          )}
        </Button>
        <p className="text-[11px] text-muted-foreground/50 text-center uppercase tracking-widest font-semibold">
          Saved scenes will appear in your editor's sidebar
        </p>
      </div>
    </motion.div>
  );
}

/**
 * Generates a placeholder opening paragraph suggestion from the beat & scenes.
 * This is a simple template-based approach — the AI version would call the backend.
 */
function generatePlaceholderParagraph(
  chapterName: string,
  chapterDescription: string | undefined,
  scenes: SceneCard[]
): string {
  const firstScene = scenes[0];

  if (firstScene?.description) {
    return `${firstScene.description.charAt(0).toUpperCase()}${firstScene.description.slice(1)}${firstScene.description.endsWith('.') ? '' : '.'}`;
  }

  if (chapterDescription) {
    return `${chapterDescription.charAt(0).toUpperCase()}${chapterDescription.slice(1)}${chapterDescription.endsWith('.') ? '' : '.'}`;
  }

  if (chapterName) {
    return `The story begins with ${chapterName.toLowerCase()}. Everything is about to change.`;
  }

  return 'The chapter begins here. Start writing your story.';
}
